/* eslint-disable @typescript-eslint/no-explicit-any */
import Vue from "vue";
import Swiper, { Navigation, Pagination, Autoplay, } from "swiper";
Swiper.use([Pagination, Navigation, Autoplay]);
const defaultOptions = {
    direction: "horizontal",
    loop: true,
    pagination: {
        el: ".swiper-pagination",
        type: "bullets",
        clickable: true,
    },
    navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
    },
};
export default Vue.extend({
    name: "Carousel",
    props: {
        value: {
            type: Number,
            default: null,
        },
        name: {
            type: String,
            default: null,
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        variant: {
            type: String,
            default: "default",
        },
        btnMiddle: {
            type: Boolean,
            default: false,
        },
        gradientEffect: {
            type: Boolean,
            default: false,
        },
        progressBar: {
            type: Boolean,
            default: false,
        },
        autoplay: {
            type: Boolean,
            default: false,
        },
        autoplayDelay: {
            type: Number,
            default: 5000,
        },
        hidePagination: {
            type: Boolean,
            default: false,
        },
        hideNext: {
            type: Boolean,
            default: false,
        },
        hidePrev: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        identifier() {
            var _a;
            return ((_a = this.name) !== null && _a !== void 0 ? _a : `carousel-${Math.floor(Math.random() * 1e6).toString(36)}`);
        },
        autoplayOptions() {
            return {
                autoplay: {
                    delay: this.autoplayDelay,
                    disableOnInteraction: false,
                },
                watchSlidesProgress: true,
            };
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                var _a;
                this.activeIndex = val;
                (_a = this.swiper) === null || _a === void 0 ? void 0 : _a.slideTo(val);
            },
        },
    },
    data: () => ({
        swiper: null,
        activeIndex: 0,
    }),
    mounted() {
        if (this.$slots.default) {
            this.swiper = new Swiper(`#${this.identifier}`, {
                ...defaultOptions,
                ...(this.autoplay ? this.autoplayOptions : {}),
                ...this.options,
            });
            this.swiper.on("activeIndexChange", (e) => {
                this.activeIndex = e.activeIndex;
                this.$emit("input", e.activeIndex);
            });
            if (this.autoplay) {
                this.registerMouseEvent();
            }
        }
    },
    methods: {
        registerMouseEvent() {
            // https://codepen.io/bjlashbrook/pen/byXzdB
            document.addEventListener("mouseenter", (event) => {
                var _a, _b, _c;
                const el = event.target;
                if (el && el.matches && el.matches(".swiper-container")) {
                    (_b = (_a = el === null || el === void 0 ? void 0 : el.swiper) === null || _a === void 0 ? void 0 : _a.autoplay) === null || _b === void 0 ? void 0 : _b.stop();
                    (_c = el === null || el === void 0 ? void 0 : el.classList) === null || _c === void 0 ? void 0 : _c.add("swiper-paused");
                    const activeNavItem = el === null || el === void 0 ? void 0 : el.querySelector(".swiper-pagination-bullet-active");
                    activeNavItem.style.animationPlayState = "paused";
                }
            }, true);
            document.addEventListener("mouseleave", (event) => {
                var _a, _b, _c, _d;
                const el = event.target;
                if (el && el.matches && el.matches(".swiper-container")) {
                    (_b = (_a = el === null || el === void 0 ? void 0 : el.swiper) === null || _a === void 0 ? void 0 : _a.autoplay) === null || _b === void 0 ? void 0 : _b.start();
                    (_c = el === null || el === void 0 ? void 0 : el.classList) === null || _c === void 0 ? void 0 : _c.remove("swiper-paused");
                    const activeNavItem = el === null || el === void 0 ? void 0 : el.querySelector(".swiper-pagination-bullet-active");
                    (_d = activeNavItem === null || activeNavItem === void 0 ? void 0 : activeNavItem.classList) === null || _d === void 0 ? void 0 : _d.remove("swiper-pagination-bullet-active");
                    setTimeout(() => {
                        var _a;
                        (_a = activeNavItem === null || activeNavItem === void 0 ? void 0 : activeNavItem.classList) === null || _a === void 0 ? void 0 : _a.add("swiper-pagination-bullet-active");
                    }, 10);
                }
            }, true);
        },
    },
    render(h) {
        var _a, _b;
        const filteredDefaultSlotItems = (_b = (_a = this.$slots.default) === null || _a === void 0 ? void 0 : _a.filter((vnode) => !!vnode.tag)) !== null && _b !== void 0 ? _b : [];
        if (filteredDefaultSlotItems.length <= 1) {
            return h("div", {}, [...filteredDefaultSlotItems]);
        }
        const items = filteredDefaultSlotItems.map((vnode) => h("div", { class: "swiper-slide" }, [vnode]));
        const interactions = h("div", {
            class: {
                "swiper-interactions": !this.btnMiddle,
                "swiper-interactions-white": this.variant === "white",
                "swiper-btn-middle": this.btnMiddle,
            },
        }, [
            h("div", { class: !this.hidePrev ? "swiper-button-prev" : "" }),
            h("div", {
                class: !this.hidePagination
                    ? "swiper-pagination"
                    : "swiper-pagination hidden",
            }),
            h("div", { class: !this.hideNext ? "swiper-button-next" : "" }),
        ]);
        const progressBarInteractions = h("div", {
            class: {
                "swiper-interactions": true,
                "swiper-interactions-progress-bar": this.progressBar,
            },
        }, [
            h("div", { class: "flex flex-col sm:flex-row items-center mb-8" }, [
                h("div", { class: "swiper-pagination" }),
                h("div", { class: "flex flex-row items-center justify-center" }, [
                    h("div", { class: "swiper-button-prev" }),
                    h("div", { class: "swiper-button-next" }),
                ]),
            ]),
        ]);
        const wrapper = h("div", { class: "swiper-wrapper" }, [items]);
        const container = h("div", {
            class: [
                "swiper-container",
                {
                    "swiper-gradient-effect": this.gradientEffect,
                },
            ],
            attrs: { id: this.identifier },
        }, [wrapper]);
        if (this.progressBar) {
            return h("div", { class: "swiper-container", attrs: { id: this.identifier } }, [wrapper, progressBarInteractions]);
        }
        return h("div", { class: "relative" }, [container, interactions]);
    },
});
